import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import { ANY_SPORT_PLAYERS_SLUG_CARDS } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { glossary } from '@sorare/core/src/lib/glossary';

import { BenchObjectTableLastCell } from 'components/benchObject/BenchObjectTable/ui';
import { BuyLowestPrice } from 'components/card/BuyLowestPrice';
import useGetTokenSingleSaleDetails from 'hooks/offers/useGetTokenSingleSaleDetails';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';

import { StartingPrice } from '../StartingPrice';
import { StartingPriceCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: StartingPriceCell_anyPlayer;
  rarity: Rarity;
  inSeason: boolean;
  onListingsClick: () => void;
};

export const StartingPriceCell = ({
  anyPlayer,
  rarity,
  inSeason,
  onListingsClick,
}: Props) => {
  const {
    flags: { usePlayerMarketDrawerInScoutingCenter = false },
  } = useFeatureFlags();
  const { up: isLaptopOrAbove } = useScreenSize('laptop');
  const { generateSportContextPath } = useSportContext();
  const { lowestPriceAnyCard } = anyPlayer;
  const offer = useGetTokenSingleSaleDetails(lowestPriceAnyCard);
  const primaryBuy = useIsPrimaryBuyLive(lowestPriceAnyCard);
  const monetaryAmount = (primaryBuy || offer)?.price;
  return (
    <BenchObjectTableLastCell isLaptopOrAbove={isLaptopOrAbove}>
      {monetaryAmount && (
        <StartingPrice
          small={!isLaptopOrAbove}
          column={!isLaptopOrAbove}
          monetaryAmount={monetaryAmount}
        />
      )}

      {isLaptopOrAbove && (
        <Horizontal gap={1}>
          {usePlayerMarketDrawerInScoutingCenter ? (
            <Button
              color="secondary"
              size="small"
              role="link"
              startIcon={<NewScarcityIcon rarity={rarity} size={2} />}
              onClick={onListingsClick}
            >
              <FormattedMessage {...glossary.listings} />
            </Button>
          ) : (
            <>
              {lowestPriceAnyCard && (
                <BuyLowestPrice size="small" card={lowestPriceAnyCard} />
              )}
              <Button
                color="secondary"
                size="small"
                role="link"
                startIcon={<NewScarcityIcon rarity={rarity} size={2} />}
                to={`${generateSportContextPath(ANY_SPORT_PLAYERS_SLUG_CARDS, {
                  params: {
                    slug: anyPlayer.slug,
                  },
                })}?${buildFilterQuery({
                  [SEARCH_PARAMS.IN_SEASON]: inSeason,
                  [SEARCH_PARAMS.ON_SALE]: true,
                  [SEARCH_PARAMS.RARITY]: rarity,
                })}`}
              >
                <FormattedMessage {...glossary.listings} />
              </Button>
            </>
          )}
        </Horizontal>
      )}
    </BenchObjectTableLastCell>
  );
};

StartingPriceCell.fragments = {
  anyPlayer: gql`
    fragment StartingPriceCell_anyPlayer on AnyPlayerInterface {
      slug
      lowestPriceAnyCard(rarity: $rarity, inSeason: $inSeason) {
        slug
        rarityTyped
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
        ...BuyLowestPrice_anyCard
      }
    }
    ${BuyLowestPrice.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
  ` as TypedDocumentNode<StartingPriceCell_anyPlayer>,
};
